import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Row, Col, Form, notification, DatePicker, Popconfirm } from 'antd';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { useQuery } from 'react-query';
import {
  addAssetCatalogAmount,
  getAssetCatalogAmountList,
  removeAssetCatalogAmount,
  updateAssetCatalogAmount,
} from 'services/assetconfig';
// import AssetCatalogStatusButton from './components/AssetCatalogStatusButton';
import { useNavigate } from 'react-router-dom';
import IAdd from 'icons/IAdd';
import SaveButtonIcon from 'components/SaveBtn';
import DeleteButton from 'components/DeleteButton';
import ButtonOperation from 'components/ButtonOperation';
import { indexBy, toDatePickerDate } from 'utils/utils2';
import Value from 'components/Value';
import InputCurrency from 'components/InputCurrency';
import moment from 'moment';
import { useFormList } from 'hooks/useFormList';
import { AuthContext } from 'components/Auth/Auth';

const AssetCatalogAmount = (props: {
  entityid: number;
  entitytype: string;
  currcode: string;
}) => {
  const { entityid, entitytype, currcode } = props;
  const navigate = useNavigate();
  const fieldName = 'assetcatalogamount';
  const [form] = Form.useForm();
  const NEW_AMOUNT_ID = -1;
  const { isInspect } = useContext(AuthContext);

  const [refetchDuplicate, setRefetchDuplicate] = useState(false);

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'associd',
    addAsFirst: true,
  });

  const [statetype, setStateType] = useState<any>('');

  const { data, isFetching, refetch } = useQuery(
    [
      'getAssetCatalogListByCriteria',
      { entityid: entityid, entitytype: entitytype },
    ],
    () =>
      getAssetCatalogAmountList({
        entityid: Number(entityid),
        entitytype: entitytype,
      }).then((res) => res.list || []),
    {
      refetchOnWindowFocus: false,
      enabled: !isNaN(entityid),
    }
  );

  const onSaveOrEdit = useCallback(
    (record: any) => {
      const assetamount: any[] = form.getFieldValue(fieldName);
      const index = indexBy(assetamount, 'associd')[record.associd];

      const currentValue = {
        ...assetamount[index],
        useridcreation: Number(localStorage.getItem('userid')) ?? 0,
        startdate: assetamount[index]?.startdate
          ? moment(assetamount[index]?.startdate).format('YYYY-MM-DD') +
            'T05:00:00.000Z'
          : '',
        enddate: assetamount[index]?.enddate
          ? moment(assetamount[index]?.enddate).format('YYYY-MM-DD') +
            'T05:00:00.000Z'
          : '',
      };

      const { associd } = currentValue;

      associd === NEW_AMOUNT_ID
        ? addAssetCatalogAmount({
            req: currentValue,
            catalogid: entityid,
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          })
        : updateAssetCatalogAmount({
            req: currentValue,
            catalogid: entityid,
            associd: associd,
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          });
    },
    [form]
  );

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'associd',
        width: 40,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('associd')(record)}>
              <Value />
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'entityamount',
        width: 110,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('entityamount')(record)}>
              <InputCurrency currcode={currcode} />
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'startdate',
        width: 110,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('startdate')(record)}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          );
        },
      },
      {
        dataIndex: 'enddate',
        width: 110,
        render: (value, record) => {
          return (
            <Form.Item noStyle name={getName('enddate')(record)}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          );
        },
      },
      {
        key: 'actions',
        render: (_, _record) => (
          <Row justify="center">
            <Col>
              <SaveButtonIcon
                onClick={(ev: any) => {
                  ev.preventDefault();
                  onSaveOrEdit(_record);
                }}
                onlyIcon
              />
            </Col>
            <Col>
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  _record.associd === NEW_AMOUNT_ID
                    ? remove(_record.associd)
                    : removeAssetCatalogAmount({
                        associd: _record.associd,
                      }).then((response) => {
                        if (response.code === '200') {
                          notification.success({
                            message: response.message,
                          });
                          refetch();
                        }
                      });
                }}
              >
                {' '}
                <DeleteButton onlyIcon />
              </Popconfirm>
            </Col>
          </Row>
        ),
        width: 70,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, refetch, statetype, refetchDuplicate]
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data.map((e) => {
          return {
            ...e,
            startdate: toDatePickerDate(e?.startdate),
            enddate: toDatePickerDate(e?.enddate),
          };
        }),
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data, refetch]);

  const getName = useCallback(
    (name: string) => (record: any) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'associd')[record.associd];
      return [fieldName, index, name];
    },
    [form]
  );

  const addRow = () => {
    const row: any = {
      associd: -1,
      creationdate: moment(Date.now()),
      enddate: '',
      entityamount: 0,
      entityid: entityid,
      entitytype: entitytype,
      startdate: '',
      taskid: 0,
      useridcreation: Number(localStorage.getItem('userid')) ?? 0,
    };
    add(row);
  };

  return (
    <Form form={form}>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const assetCatalog_list: any[] = getFieldValue(fieldName);
          const isNewItemInTable =
            assetCatalog_list?.find(
              ({ associd }) => associd === NEW_AMOUNT_ID
            ) !== undefined;
          return (
            <>
              <Row justify="end" style={{ marginBottom: '-16px' }}>
                <ButtonOperation
                  icon={<IAdd color="white" />}
                  type="primary"
                  comcode="add"
                  htmlType="button"
                  disabled={isNewItemInTable}
                  onClick={() => {
                    !isInspect && addRow();
                  }}
                />
              </Row>
              <DataGrid
                dataSource={assetCatalog_list}
                columns={columns}
                pagination={
                  assetCatalog_list?.length < 5 ? false : { pageSize: 5 }
                }
                isLoading={isFetching}
                rowKey={(record) => record.associd}
                size="small"
                hideFilters
                hideExtraAction
              />
            </>
          );
        }}
      </Form.Item>
    </Form>
  );
};

export default AssetCatalogAmount;
